import { MailOutlined } from '@ant-design/icons';
import { getSessionToken, getSlugFromUrl } from '@seaters-app/data-access';
import Layout, { Header } from 'antd/es/layout/layout';
import {
  Link,
  Navigate,
  Outlet,
  useLocation,
  useSearchParams,
} from 'react-router-dom';
import './PublicLayout.css';
import {
  Avatar,
  Button,
  ConfigProvider,
  Grid,
  Select,
  Space,
  Typography,
  theme,
} from 'antd';
import { useFanGroupData } from './hooks';
import { useEffect } from 'react';
import {
  LOCAL_STORAGE_SLUG,
  SESSION_STORAGE_TARGET_APP,
  LOCAL_STORAGE_USER_LOCALE,
  TranslationsTarget,
  routes,
  Language,
  SESSION_STORAGE_ALLOWED_GROUPS,
  languagesOptions,
  userLanguagesOptions,
  SESSION_STORAGE_VERIFICATION,
  LOCATION_STATES,
} from '@seaters-app/constants';
import { Container, primaryColor } from '@seaters-app/ui';
import { useTranslation } from 'react-i18next';
import Banner from '../../waiting-lists/components/Banner/Banner';
import { useAppTranslationsChange } from '../../../utils/hooks';
import AppFooter from '../Footer/Footer';
import { CookiesBanner } from '@seaters-app/ui-shared';
import { useFanGroupSlug } from '../../hooks';

const { Text } = Typography;
const { useBreakpoint } = Grid;

const PublicLayout = () => {
  const auth = getSessionToken();
  const { t, i18n, ready } = useTranslation();
  const { language } = i18n;
  const lang = language === 'nl_NL' || language === 'nl_BE' ? 'nl' : language;
  const [searchParams] = useSearchParams();
  const prefilledLang = searchParams.get('lang') ?? null;
  const isAllowedGroups = sessionStorage.getItem(
    SESSION_STORAGE_ALLOWED_GROUPS
  );

  const { state: locationState } = useLocation();

  const { changeTranslations } = useAppTranslationsChange();

  const { token } = theme.useToken();

  const detectedSlug = getSlugFromUrl();

  const slug = localStorage.getItem(LOCAL_STORAGE_SLUG) ?? detectedSlug;
  const userData = sessionStorage.getItem(SESSION_STORAGE_VERIFICATION);
  const roles = userData ? JSON.parse(userData)?.userData?.roles || [] : [];

  const { xs } = useBreakpoint();

  // Language detection that respects browser detection for Dutch variants
  const getStoredLanguage = () => {
    // Check URL parameter first (highest priority)
    if (prefilledLang) {
      return prefilledLang;
    }

    // Check localStorage (user preference)
    const storedLocale = localStorage
      .getItem(LOCAL_STORAGE_USER_LOCALE)
      ?.replace(/"/g, '');
    if (storedLocale) {
      return storedLocale;
    }

    // Check i18next detected language (includes browser detection and domain overrides)
    const detectedLang = sessionStorage
      .getItem('i18nextLng')
      ?.replace(/"/g, '');
    if (detectedLang) {
      return detectedLang;
    }

    return 'en'; // Default fallback
  };

  const currentLanguage = getStoredLanguage();
  const existLang =
    Object.values(Language).find((el) => el === currentLanguage) ?? 'en';

  const { profileImage, fanGroupName, coverImage, helpUrls, faqLinks } =
    useFanGroupData();

  useEffect(() => {
    if (
      detectedSlug &&
      detectedSlug !== localStorage.getItem(LOCAL_STORAGE_SLUG)
    ) {
      localStorage.setItem(LOCAL_STORAGE_SLUG, detectedSlug);
    }
  }, [detectedSlug]);

  useEffect(() => {
    document.title = fanGroupName[lang] || 'Seaters';
    return () => {
      document.title = 'Seaters';
    };
  }, [fanGroupName, lang]);

  const setCurrentLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
  };

  // if (!auth && !detectedSlug) {
  //   localStorage.removeItem(LOCAL_STORAGE_SLUG);
  // }

  const { customLightTheme } = useFanGroupSlug();

  const isLoading =
    !ready ||
    (!!detectedSlug && customLightTheme.token.colorPrimary === primaryColor);

  useEffect(() => {
    if (
      sessionStorage.getItem(SESSION_STORAGE_TARGET_APP) !==
      TranslationsTarget.WEB
    ) {
      changeTranslations(SESSION_STORAGE_TARGET_APP, TranslationsTarget.WEB);
    }
  }, []);

  useEffect(() => {
    changeTranslations(LOCAL_STORAGE_USER_LOCALE, existLang);
  }, [existLang]);

  useEffect(() => {
    if (roles.length) {
      window.location.reload();
    }
  }, [roles]);

  return auth && detectedSlug ? (
    <Navigate
      to={`${
        locationState?.[LOCATION_STATES.REDIRECT_PATH]?.pathname
          ? locationState?.[LOCATION_STATES.REDIRECT_PATH]?.pathname
          : `/${detectedSlug}`
      }`}
    />
  ) : (
    <Layout className="publicLayout">
      {profileImage && slug && (
        <Header
          style={{
            backgroundColor: token.colorPrimaryBg,
            padding: 0,
          }}
        >
          <Container>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Space
                style={{
                  marginRight: 'auto',
                }}
              >
                <Link to={`./${routes.landing}`}>
                  <Avatar size="large" src={profileImage} />
                </Link>
                {!xs && (
                  <Text strong>
                    {fanGroupName[existLang] ?? fanGroupName?.en}
                  </Text>
                )}
              </Space>
              {ready && (
                <Space size={xs ? 12 : 16} align="center">
                  <Link
                    to={faqLinks ? faqLinks : t('footer_support_faq-link')}
                    target="_blank"
                    style={{ marginRight: xs ? 8 : 12 }}
                  >
                    <Text style={{ fontSize: xs ? 12 : 14 }} strong>
                      {t('footer_support_faq')}
                    </Text>
                  </Link>
                  <Link
                    to={helpUrls ? helpUrls : t('helpdesk_url')}
                    target="_blank"
                  >
                    <Space size={4} align="center">
                      <MailOutlined size={xs ? 10 : 14} rev={undefined} />
                      <Text style={{ fontSize: xs ? 12 : 14 }} strong>
                        {t('footer_support_contact')}
                      </Text>
                    </Space>
                  </Link>
                  <ConfigProvider
                    theme={{
                      components: {
                        Select: {
                          fontSize: xs ? 12 : 14,
                        },
                      },
                    }}
                  >
                    <Select
                      size="small"
                      variant="borderless"
                      defaultValue={existLang}
                      options={userLanguagesOptions}
                      optionRender={({ data }) => {
                        return (
                          <Text
                            style={{
                              fontSize: xs ? 12 : 14,
                            }}
                          >
                            {data?.label}
                          </Text>
                        );
                      }}
                      style={{
                        width: xs ? '130px' : '145px',
                        fontWeight: 'bold',
                      }}
                      onChange={setCurrentLanguage}
                    />
                  </ConfigProvider>
                </Space>
              )}
            </div>
          </Container>
        </Header>
      )}
      <div
        style={{
          marginBottom: '30px',
        }}
      >
        {slug && <Banner image={coverImage ?? ''} />}
      </div>
      {ready && <Outlet />}
      {ready && !isAllowedGroups && <AppFooter />}
      {!isLoading && <CookiesBanner />}
    </Layout>
  );
};

export { PublicLayout };
